import React, { useState, useEffect, useCallback } from 'react';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import { CRow, CCol, CBadge } from '@coreui/react';
import CLabel from '../../../../../migration/CLabel';

import { callTokenApi } from '../../../../../../apiCaller';
import { formatDate, toastError } from '../../../../../../utils';
import { ADMIN_CENTRALIZED_COMPANY_RETRIEVE, DEFAULT_DATE_TIME_FORMAT } from '../../../../../../constants';
import CompanyIdentifiersPanel from '../tabs/CompanyIdentifiersPanel';

const renderBoolean = (value, trueText = "Yes", falseText = "No", undefinedText = "none") => {
    if (typeof value === 'boolean') {
        return value ? <CBadge color="success" shape="rounded-pill">{trueText}</CBadge> : <CBadge color="danger" shape="rounded-pill">{falseText}</CBadge>;
    }
    return <CBadge color="secondary" shape="rounded-pill">{undefinedText}</CBadge>;
};

const renderJsonData = (jsonData, fieldName = "Data") => {
    if (jsonData === null || jsonData === undefined) return <small className="text-muted">none</small>;
    if (typeof jsonData === 'object') {
        if (Array.isArray(jsonData) && jsonData.length === 0) return <small className="text-muted">Empty</small>;
        return (
            <details className="json-details mb-2">
                <summary style={{ cursor: 'pointer', fontWeight: '500' }}>View {fieldName} (JSON)</summary>
                <pre style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    background: '#f8f9fa',
                    padding: '10px',
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}>
                    {JSON.stringify(jsonData, null, 2)}
                </pre>
            </details>
        );
    }
    return String(jsonData);
};

const getStatusBadgeColor = (status) => {
    if (!status) return 'light';
    const lowerStatus = String(status).toLowerCase();
    if (lowerStatus.includes('valid') || lowerStatus.includes('processed') || lowerStatus.includes('enriched') || lowerStatus.includes('active')) return 'success';
    if (lowerStatus.includes('invalid') || lowerStatus.includes('failed') || lowerStatus.includes('error')) return 'danger';
    if (lowerStatus.includes('pending') || lowerStatus.includes('unverified') || lowerStatus.includes('unknown') || lowerStatus.includes('prospect')) return 'warning';
    if (lowerStatus.includes('potential') || lowerStatus.includes('tentative') || lowerStatus.includes('stealth')) return 'info';
    return 'secondary';
};

const CentralizedCompany = ({ attributes }) => {
    const { id: companyId } = attributes;

    const [companyData, setCompanyData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!companyId) {
            setError("Person ID is missing.");
            setIsLoading(false);
            setCompanyData(null);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await callTokenApi(`${ADMIN_CENTRALIZED_COMPANY_RETRIEVE.replace(':id', companyId)}`, 'GET', null);

            if (response.status === 200 && response.data?.success) {
                setCompanyData(response.data.data);
            } else {
                const errorMsg = response.data?.message || `Failed to fetch person data (status: ${response.status})`;
                setError(errorMsg);
                toastError({ data: { message: errorMsg } });
                setCompanyData(null);
            }
        } catch (err) {
            console.error("API call failed for person detail:", err);
            const errorMessage = err.message || 'An unexpected error occurred.';
            setError(errorMessage);
            toastError({ data: { message: errorMessage } });
            setCompanyData(null);
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (isLoading) {
        return <CenterSpinner />;
    }

    if (error) {
        return <div className="alert alert-danger" role="alert">Error: {error}</div>;
    }

    if (!companyData) {
        return <div className="alert alert-info">No person data found for ID: {companyId}</div>;
    }

    const fieldsToDisplay = [
        { label: "Company Name", value: companyData.name },
        { label: "Domain", value: companyData.domain, type: 'urlDomain' },
        { label: "Status", value: companyData.status, type: 'statusBadge' },
        { label: "Quality Score", value: companyData.qualityScore?.toFixed(2) },
        { label: "LLM Run", value: renderBoolean(companyData.runLLM, "Processed", "Not Processed") },
        { label: "Valid Domain Status", value: companyData.validDomain, type: 'statusBadge' },
        { label: "Valid LinkedIn Status", value: companyData.validLinkedin, type: 'statusBadge' },
        { label: "Enriched", value: renderBoolean(companyData.enriched) },
        { label: "Valid Person Match", value: renderBoolean(companyData.validPersonMatch, "Matched", "Not Matched") },
        { label: "Invalid Person Match", value: renderBoolean(companyData.invalidPersonMatch, "Invalid Matches", "No Invalid Matches") },
        { label: "Company LLM Data", value: renderJsonData(companyData.companyLLM, "Company LLM") },
        { label: "Metadata", value: renderJsonData(companyData.metadata, "Metadata") },
        { label: "Raw Data", value: renderJsonData(companyData.rawData, "Raw Data") },
        { label: "Created At", value: formatDate(companyData.created_at, DEFAULT_DATE_TIME_FORMAT) },
        { label: "Updated At", value: formatDate(companyData.updated_at, DEFAULT_DATE_TIME_FORMAT) },
    ];

    const renderFieldValue = (field) => {
        if (field.type === 'urlDomain' && field.value) {
            return <a href={`http://${field.value}`} target="_blank" rel="noopener noreferrer">{field.value}</a>;
        }
        if (field.type === 'statusBadge' && field.value) {
            return <CBadge color={getStatusBadgeColor(field.value)} shape="rounded-pill">{field.value}</CBadge>;
        }
        if (typeof field.value === 'object' && field.value !== null && !React.isValidElement(field.value)) {
            return JSON.stringify(field.value);
        }
        return field.value ?? <small className="text-muted">none</small>;
    };

    return (
        <div className="rule-detail">
            <h3 className='mb-4'>
                Company Details: {companyData.name || 'none'}
            </h3>

            <CRow>
                {fieldsToDisplay.map(field => (
                    <React.Fragment key={field.label}>
                        <CCol md="3" className="mb-2">
                            <CLabel className="fw-semibold">{field.label}:</CLabel>
                        </CCol>

                        <CCol md="9" className="mb-2">
                            {renderFieldValue(field)}
                        </CCol>
                    </React.Fragment>
                ))}

                <CCol md="12">
                    <CompanyIdentifiersPanel
                        account={{ id: companyData.accountId }}
                        companyId={companyData.id}
                        title='Company Identifiers'
                    />
                </CCol>
            </CRow>
        </div>
    );
};

export default CentralizedCompany;